import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { FONT_SIZE, INK, MOTION, NEUTRAL, STEP_HEADER, SURFACE } from "@/theme/tokens";

type Props = {
	/** The step's position in the sequence, shown in the badge. */
	step: number;
	/** Already-translated step question/title ("Which shift?"). */
	title: string;
	/**
	 * Inline context after the title — a count, the chosen value ("· 24 available · Tue, Sep 22"). Only
	 * rendered while the step is reachable: a disabled step has nothing to summarise yet.
	 */
	meta?: ReactNode;
	/**
	 * Controls that belong to the step but read as its scope rather than its answer (a job picker, a
	 * location), pushed to the end of the heading row. Wraps under the title on a narrow panel.
	 */
	trailing?: ReactNode;
	/** Not reachable yet (an earlier step is unanswered): muted badge + title, `aria-disabled` on the heading. */
	disabled?: boolean;
	/** Heading level for the title, so a panel's steps sit correctly in the document outline. Defaults to h3. */
	component?: "h2" | "h3" | "h4";
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * The heading of one step in a multi-step panel: a numbered circle, the step's question and an optional
 * inline summary. The badge fills with the brand colour once the step is reachable and recedes to a muted
 * neutral before it, crossfading as the earlier step gets answered — so a panel of steps reads as a
 * sequence, and "you can't do this yet" is visible before anyone tries.
 *
 * Generic over what the steps are (pick a shift → pick staff, pick a file → map columns): the caller owns
 * the step body and decides when a step is `disabled`. For progress through a long flow use
 * `SystemStageTrack`; for a titled settings block use `SettingsSection`.
 */
export const StepHeader: FC<Props> = ({ step, title, meta, trailing, disabled = false, component = "h3", dataTestId, sx }) => (
	<Box
		data-testid={dataTestId}
		aria-disabled={disabled || undefined}
		sx={[{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.25, rowGap: 1, minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}
	>
		<Box
			component="span"
			aria-hidden
			sx={(theme) => ({
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				flexShrink: 0,
				width: STEP_HEADER.BADGE_SIZE,
				height: STEP_HEADER.BADGE_SIZE,
				borderRadius: "50%",
				fontSize: FONT_SIZE.SECONDARY,
				fontWeight: 700,
				fontVariantNumeric: "tabular-nums",
				backgroundColor: disabled ? NEUTRAL[200] : theme.palette.primary.main,
				color: disabled ? NEUTRAL[500] : SURFACE,
				transition: theme.transitions.create(["background-color", "color"], { duration: MOTION.DURATION.COLOR_SHIFT, easing: MOTION.EASE_OUT }),
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			})}
		>
			{step}
		</Box>
		<Typography
			component={component}
			variant="subtitle1"
			sx={{
				m: 0,
				flexShrink: 0,
				fontWeight: 600,
				lineHeight: 1.3,
				color: disabled ? NEUTRAL[500] : INK.PRIMARY,
				transition: `color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
			}}
		>
			{title}
		</Typography>
		{meta && !disabled && (
			<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0, fontSize: FONT_SIZE.EMPHASIS, color: INK.MUTED }}>
				{meta}
			</Box>
		)}
		{trailing && <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, marginInlineStart: "auto", minWidth: 0 }}>{trailing}</Box>}
	</Box>
);

export default StepHeader;
